import { useState, useCallback } from 'react';
import { GuessHistoryItem } from '../types/types';
import { calculatePoints } from '../utils/scoring';
import { CSS_CLASSES } from '../constants';

export interface YearGuessGameState {
  yearInput: string[];
  hasGuessed: boolean;
  guessHistory: GuessHistoryItem[];
  validationError: string;
  revealedDigits: boolean[];
  selectingDigit: boolean;
  hintRequested: boolean;
  hintMessage: string;
  score: number;
  streak: number;
}

export interface YearGuessGameConfig {
  minYear: number;
  maxYear: number;
  initialState?: Partial<YearGuessGameState>;
}

export const useYearGuessGame = ({
  minYear,
  maxYear,
  initialState = {},
}: YearGuessGameConfig) => {
  // Log the received initial state

  const [state, setState] = useState<YearGuessGameState>(() => {
    const defaultState: YearGuessGameState = {
      yearInput: ['', '', '', ''],
      hasGuessed: false,
      guessHistory: [],
      validationError: '',
      revealedDigits: [false, false, false, false],
      selectingDigit: false,
      hintRequested: false,
      hintMessage: '',
      score: 0,
      streak: 0,
    };
    const finalInitialState = { ...defaultState, ...initialState };
    // Log the actual state being set

    return finalInitialState;
  });

  // Handle slider change
  const handleSliderChange = useCallback(
    (year: number) => {
      if (state.hasGuessed) return;

      // Create a year string that respects revealed digits
      const yearString = year.toString().padStart(4, '0');
      let newYearInput = [...state.yearInput];

      for (let i = 0; i < 4; i++) {
        // Only update non-revealed digits
        if (!state.revealedDigits[i]) {
          newYearInput[i] = yearString[i];
        }
      }

      setState((prev) => ({
        ...prev,
        yearInput: newYearInput,
        validationError: '',
      }));
    },
    [state.hasGuessed, state.revealedDigits, state.yearInput]
  );

  // Handle year digit input
  const handleDigitInput = useCallback(
    (index: number, value: string) => {
      if (state.hasGuessed) return;
      if (state.revealedDigits[index]) return;
      if (value && !/^\d$/.test(value)) return;

      const newYearInput = [...state.yearInput];
      newYearInput[index] = value;
      setState((prev) => ({
        ...prev,
        yearInput: newYearInput,
        validationError: '',
      }));

      if (value && index < 3) {
        const nextInput = document.getElementById(
          `${CSS_CLASSES.YEAR_INPUT_PREFIX}${index + 1}`
        );
        if (nextInput) nextInput.focus();
      }
    },
    [state.hasGuessed, state.revealedDigits, state.yearInput]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (state.hasGuessed) return;
      if (e.key === 'Backspace' && state.yearInput[index] === '') {
        if (index > 0) {
          const prevInput = document.getElementById(
            `${CSS_CLASSES.YEAR_INPUT_PREFIX}${index - 1}`
          );
          if (prevInput) prevInput.focus();
        }
      } else if (e.key === 'ArrowLeft') {
        if (index > 0) {
          const prevInput = document.getElementById(
            `${CSS_CLASSES.YEAR_INPUT_PREFIX}${index - 1}`
          );
          if (prevInput) prevInput.focus();
        }
      } else if (e.key === 'ArrowRight') {
        if (index < 3) {
          const nextInput = document.getElementById(
            `${CSS_CLASSES.YEAR_INPUT_PREFIX}${index + 1}`
          );
          if (nextInput) nextInput.focus();
        }
      }
    },
    [state.hasGuessed, state.yearInput]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (state.hasGuessed) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const yearRegex = /^\d{4}$/;
      if (yearRegex.test(pastedData)) {
        const digits = pastedData.split('');
        const newYearInput = [...state.yearInput];
        for (let i = 0; i < 4; i++) {
          if (!state.revealedDigits[i]) {
            newYearInput[i] = digits[i];
          }
        }
        setState((prev) => ({
          ...prev,
          yearInput: newYearInput,
          validationError: '',
        }));
      }
    },
    [state.hasGuessed, state.revealedDigits, state.yearInput]
  );

  // Allow clicking on the timeline to update input
  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (state.hasGuessed) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      const yearRange = maxYear - minYear;
      let year = Math.round(minYear + yearRange * clickPosition);

      // Create a year string that respects revealed digits
      const yearString = year.toString().padStart(4, '0');
      let newYearInput = [...state.yearInput];

      for (let i = 0; i < 4; i++) {
        // Only update non-revealed digits
        if (!state.revealedDigits[i]) {
          newYearInput[i] = yearString[i];
        }
      }

      setState((prev) => ({
        ...prev,
        yearInput: newYearInput,
        validationError: '',
      }));
    },
    [state.hasGuessed, state.revealedDigits, state.yearInput, minYear, maxYear]
  );

  // Validation and submission of guess
  const validateGuess = useCallback((): {
    isValid: boolean;
    error?: string;
  } => {
    if (state.yearInput.some((digit) => digit === '')) {
      return { isValid: false, error: 'Please enter a complete 4-digit year' };
    }

    const guessedYear = parseInt(state.yearInput.join(''));
    if (guessedYear < minYear || guessedYear > maxYear) {
      return {
        isValid: false,
        error: `Year must be between ${minYear} and ${maxYear}`,
      };
    }

    return { isValid: true };
  }, [state.yearInput, minYear, maxYear]);

  const makeGuess = useCallback(
    (actualYear: number) => {
      if (state.hasGuessed) return null;

      const validation = validateGuess();
      if (!validation.isValid) {
        setState((prev) => ({
          ...prev,
          validationError: validation.error || '',
        }));
        return null;
      }

      const guessedYear = parseInt(state.yearInput.join(''));
      const isCorrect = guessedYear === actualYear;
      const points = calculatePoints(guessedYear, actualYear);

      setState((prev) => ({
        ...prev,
        score: prev.score + points,
        validationError: '',
        hasGuessed: true,
        guessHistory: [
          ...prev.guessHistory,
          { year: guessedYear, correct: isCorrect },
        ],
      }));

      return {
        guessedYear,
        isCorrect,
        points,
        correctGuesses: isCorrect ? 1 : 0,
      };
    },
    [state.hasGuessed, state.yearInput, validateGuess]
  );

  // Toggle digit selection mode for hints
  const toggleDigitSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectingDigit: !prev.selectingDigit,
      hintMessage: '',
    }));
  }, []);

  // Reset state for a new round
  const resetRound = useCallback(() => {
    // Log when resetRound is called

    setState((prev) => ({
      ...prev,
      yearInput: ['', '', '', ''],
      hasGuessed: false,
      guessHistory: [], // <-- This resets the history
      selectingDigit: false,
      hintMessage: '',
      validationError: '',
      revealedDigits: [false, false, false, false],
    }));
  }, []);

  // Update the revealed digits when a hint is used
  const revealDigit = useCallback(
    (index: number, actualYear: number) => {
      if (state.hasGuessed || !state.selectingDigit) return;

      const actualYearStr = actualYear.toString().padStart(4, '0');
      const digit = actualYearStr[index];

      // Create a new yearInput array with ALL non-revealed digits cleared
      const newYearInput = ['', '', '', ''];

      // Only set the newly revealed digit and previously revealed digits
      for (let i = 0; i < 4; i++) {
        if (i === index) {
          // This is the newly revealed digit
          newYearInput[i] = digit;
        } else if (state.revealedDigits[i]) {
          // This is a previously revealed digit - keep its value
          newYearInput[i] = state.yearInput[i];
        }
        // All other digits remain empty
      }

      // Update the revealed digits for this round
      const newRevealedDigits = [...state.revealedDigits];
      newRevealedDigits[index] = true;

      setState((prev) => ({
        ...prev,
        yearInput: newYearInput,
        revealedDigits: newRevealedDigits,
        hintRequested: true,
        selectingDigit: false,
        hintMessage: '',
      }));

      return newRevealedDigits;
    },
    [
      state.hasGuessed,
      state.selectingDigit,
      state.revealedDigits,
      state.yearInput,
    ]
  );

  // Function to censor years from text
  const filterText = useCallback((text: string): string => {
    if (!text) return '';

    // First remove all HTML tags using a more aggressive regex
    const withoutHtml = text.replace(/<[^>]*>/g, '');

    // Then, censor all numbers in the text
    return withoutHtml.replace(/\d+/g, 'XX').trim();
  }, []);

  return {
    state,
    setState,
    calculatePoints,
    handleSliderChange,
    handleDigitInput,
    handleKeyDown,
    handlePaste,
    handleTimelineClick,
    validateGuess,
    makeGuess,
    toggleDigitSelection,
    resetRound,
    revealDigit,
    filterText,
  };
};

export default useYearGuessGame;
